import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, type IconName } from "../components/Icon.js";
import { useSurface } from "../lib/useSurface.js";
import { auth, setToken, getClaims, ApiError } from "../lib/api.js";
import { defaultRouteForRoles } from "../lib/roles.js";
import { flushQueue } from "../lib/offline.js";
import authHero from "../assets/auth-hero.jpg";

/** Read the ?expired=1 hint set by api() when an authenticated call 401s (session expired). */
function sessionExpired(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("expired") === "1";
}

/** A safe same-origin `?next=` target (or null) — guards against open redirects. */
function safeNext(): string | null {
  if (typeof window === "undefined") return null;
  const n = new URLSearchParams(window.location.search).get("next");
  if (!n) return null;
  try {
    const d = decodeURIComponent(n);
    if (d.startsWith("/") && !d.startsWith("//")) return d;
  } catch {
    /* ignore malformed */
  }
  return null;
}

/** Where to land after login: honour ?next=, else the signed-in role's default home. */
function destAfterLogin(): string {
  return safeNext() ?? defaultRouteForRoles(getClaims()?.roles ?? []);
}

/** Login — personal magic link OR store-shared iPad PIN. */
export function Auth() {
  useSurface("console");
  const navigate = useNavigate();
  // A session-expired redirect lands on the store-iPad tab with a clear notice (the common case).
  const [tab, setTab] = useState<"personal" | "store">(sessionExpired() ? "store" : "personal");
  const [mode, setMode] = useState<"login" | "signup">("login"); // personal tab: ログイン or 新規登録
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState(""); // signup: 店舗コード
  const [storeCode, setStoreCode] = useState("SHIBUYA-01");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [expired, setExpired] = useState(sessionExpired());

  // Magic-link return: when arriving at /auth/verify?token=… (the emailed link), exchange the
  // token for a JWT and sign in. A normal /auth visit has no token and skips this.
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    if (!t) return;
    setBusy(true);
    setMsg("ログイン中…");
    auth
      .verify(t)
      .then((v) => {
        setToken(v.token);
        void afterLogin(destAfterLogin());
      })
      .catch(() => setMsg("リンクが無効か、期限が切れています。もう一度ログインリンクを送信してください。"))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a successful login, push any captures that were queued while offline / signed-out
  // (e.g. a recording taken after the session silently expired) so they aren't lost.
  async function afterLogin(dest: string) {
    setExpired(false);
    try {
      await flushQueue();
    } catch {
      /* best-effort — queued items remain for the next auto-flush */
    }
    navigate(dest);
  }

  async function submitPassword() {
    if (!email.trim() || !password) {
      setMsg("メールアドレスとパスワードを入力してください。");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const v = await auth.password(email.trim(), password);
      setToken(v.token);
      await afterLogin(destAfterLogin());
    } catch {
      setMsg("メールアドレスまたはパスワードが正しくありません。");
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  async function submitSignup() {
    if (!email.trim() || !password || !joinCode.trim()) {
      setMsg("メールアドレス・パスワード・店舗コードを入力してください。");
      return;
    }
    if (password.length < 8) {
      setMsg("パスワードは8文字以上にしてください。");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const v = await auth.signup({
        name: name.trim() || undefined,
        email: email.trim(),
        password,
        storeCode: joinCode.trim(),
      });
      setToken(v.token);
      await afterLogin(destAfterLogin());
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setMsg("このメールアドレスは既に登録されています。ログインしてください。");
        setMode("login");
      } else if (e instanceof ApiError && e.message.includes("invalid_store_code")) {
        setMsg("店舗コードが見つかりません。管理者に配布されたコードを確認してください。");
      } else {
        setMsg("登録に失敗しました。通信環境を確認してもう一度お試しください。");
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitPin() {
    setBusy(true);
    setMsg(null);
    try {
      const v = await auth.storePin(storeCode.trim(), pin);
      setToken(v.token);
      await afterLogin(destAfterLogin());
    } catch {
      setMsg("店舗コードまたはPINが正しくありません。");
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  // Pad keys carry an id so handler/style logic never depends on a rendered glyph (which differs per OS).
  type PadKey =
    | { id: "back"; icon: IconName }
    | { id: "enter"; icon: IconName }
    | { id: "digit"; digit: string };
  const PAD_KEYS: PadKey[] = [
    { id: "digit", digit: "1" },
    { id: "digit", digit: "2" },
    { id: "digit", digit: "3" },
    { id: "digit", digit: "4" },
    { id: "digit", digit: "5" },
    { id: "digit", digit: "6" },
    { id: "digit", digit: "7" },
    { id: "digit", digit: "8" },
    { id: "digit", digit: "9" },
    { id: "back", icon: "backspace" },
    { id: "digit", digit: "0" },
    { id: "enter", icon: "arrow-right" },
  ];

  function pinKey(k: PadKey) {
    if (k.id === "back") setPin((p) => p.slice(0, -1));
    else if (k.id === "enter") void submitPin();
    else setPin((p) => (p.length < 4 ? p + k.digit : p));
  }

  return (
    <div className="split" style={splitStyle}>
      <div className="poster" data-surface="field" style={posterStyle}>
        {/* Photographic hero — the field operator wearing a chest-mounted phone rig (= TEBIKI's "just film it"
            promise). Sharp subject + left-weighted scrim keeps the headline legible without hiding the image. */}
        <img src={authHero} alt="" aria-hidden="true" style={posterBgStyle} />
        <span aria-hidden="true" style={posterScrimStyle} />
        <div className="brand" style={{ position: "relative", zIndex: 1 }}>
          <span className="glyph" aria-hidden="true" />
          TEBIKI
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <h2 className="display" style={{ fontWeight: 600, fontSize: "clamp(34px,5vw,60px)", lineHeight: 1, letterSpacing: "-.02em", textShadow: "0 2px 30px rgba(0,0,0,.55)" }}>
            撮る。
            <br />
            すると<span className="hl">手順</span>に。
          </h2>
          <p className="jp" style={{ color: "var(--ink)", opacity: 0.92, marginTop: 18, maxWidth: 360, textShadow: "0 1px 14px rgba(0,0,0,.6)" }}>
            店舗の業務マニュアルを、撮影するだけで。AIが手順に整えます。
          </p>
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--faint)", position: "relative", zIndex: 1, textShadow: "0 1px 10px rgba(0,0,0,.6)" }}>
          INK · PAPER · HIGHLIGHTER
        </div>
      </div>

      <div className="formside" style={{ display: "grid", placeItems: "center", padding: "40px 28px" }}>
        <div className="authcard reveal" style={{ width: "100%", maxWidth: 380 }}>
          {expired ? (
            <div
              role="status"
              className="jp"
              style={{
                marginBottom: 18,
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid var(--hi)",
                background: "color-mix(in srgb, var(--hi) 12%, transparent)",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              <b>セッションの有効期限が切れました。</b><br />
              もう一度ログインしてください。撮影済みの未送信データがあれば、ログイン後に自動で送信されます。
            </div>
          ) : null}
          <div className="segmented" role="group" aria-label="ログイン方法" style={{ width: "100%", marginBottom: 22 }}>
            <button aria-pressed={tab === "personal"} style={{ flex: 1 }} onClick={() => setTab("personal")}>
              個人ログイン
            </button>
            <button aria-pressed={tab === "store"} style={{ flex: 1 }} onClick={() => setTab("store")}>
              店舗の共有iPad
            </button>
          </div>

          {tab === "personal" && mode === "signup" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitSignup();
              }}
            >
              <h1 className="display" style={{ fontSize: 28, marginBottom: 4 }}>
                はじめまして
              </h1>
              <p className="jp" style={{ color: "var(--muted)", margin: "0 0 18px" }}>
                店舗コードがあれば、その場でアカウントを作成できます
              </p>
              <label className="field-label" htmlFor="su-name">
                名前（任意）
              </label>
              <input
                id="su-name"
                className="input jp"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田 太郎"
                style={{ marginBottom: 14 }}
              />
              <label className="field-label" htmlFor="su-email">
                メールアドレス
              </label>
              <input
                id="su-email"
                className="input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ marginBottom: 14 }}
              />
              <label className="field-label" htmlFor="su-password">
                パスワード（8文字以上）
              </label>
              <input
                id="su-password"
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{ marginBottom: 14 }}
              />
              <label className="field-label" htmlFor="su-code">
                店舗コード
              </label>
              <input
                id="su-code"
                className="input mono"
                type="text"
                autoComplete="off"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="例: SHIBUYA-01"
                style={{ marginBottom: 10 }}
              />
              <p className="jp" style={{ fontSize: 12, color: "var(--muted)", margin: "0 0 14px", lineHeight: 1.7 }}>
                店舗コードは管理者から配布されます。登録直後は<b>閲覧のみ（viewer）</b>で、
                作成・編集などの権限は管理者が「メンバー・権限」画面で付与します。
              </p>
              {msg ? (
                <p className="jp" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 12px" }} role="status">
                  {msg}
                </p>
              ) : null}
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? "登録中…" : "アカウントを作成"}
              </button>
              <button
                type="button"
                className="btn btn--block"
                style={{ marginTop: 12 }}
                onClick={() => {
                  setMode("login");
                  setMsg(null);
                }}
              >
                ログインへ戻る
              </button>
            </form>
          ) : tab === "personal" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitPassword();
              }}
            >
              <h1 className="display" style={{ fontSize: 28, marginBottom: 4 }}>
                おかえりなさい
              </h1>
              <p className="jp" style={{ color: "var(--muted)", margin: "0 0 18px" }}>
                メールアドレスとパスワードでログイン
              </p>
              <label className="field-label" htmlFor="email">
                メールアドレス
              </label>
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{ marginBottom: 14 }}
              />
              <label className="field-label" htmlFor="password">
                パスワード
              </label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{ marginBottom: 14 }}
              />
              {msg ? (
                <p className="jp" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 12px" }} role="status">
                  {msg}
                </p>
              ) : null}
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? "ログイン中…" : "ログイン"}
              </button>
              <div className="row" style={{ margin: "16px 0" }}>
                <hr className="rule grow" />
                <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
                  OR
                </span>
                <hr className="rule grow" />
              </div>
              <button
                type="button"
                className="btn btn--block"
                onClick={() => {
                  setMode("signup");
                  setMsg(null);
                }}
              >
                はじめての方 — 店舗コードで新規登録
              </button>
            </form>
          ) : (
            <div>
              <h1 className="display" style={{ fontSize: 28, marginBottom: 4 }}>
                店舗端末ログイン
              </h1>
              <p className="jp" style={{ color: "var(--muted)", margin: "0 0 14px" }}>
                店舗コードとPINでかんたんに
              </p>
              <label className="field-label" htmlFor="storecode">
                店舗コード
              </label>
              <input id="storecode" className="input mono" value={storeCode} onChange={(e) => setStoreCode(e.target.value)} style={{ marginBottom: 14 }} />
              {msg ? (
                <p className="jp" role="status" style={{ color: "var(--danger)", fontSize: 13, margin: "0 0 10px" }}>
                  {msg}
                </p>
              ) : null}
              <label className="field-label">PIN</label>
              <div className="row" style={{ gap: 8 }} aria-label={`PIN ${pin.length}/4桁入力済み`}>
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="chip mono" style={i >= pin.length ? { color: "var(--muted)" } : undefined}>
                    {i < pin.length ? <Icon name="dot-filled" size={12} /> : <Icon name="dot-empty" size={12} />}
                  </span>
                ))}
              </div>
              <div className="pinpad" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginTop: 14 }}>
                {PAD_KEYS.map((k) => {
                  const isEnter = k.id === "enter";
                  return (
                    <button
                      key={k.id === "digit" ? k.digit : k.id}
                      onClick={() => pinKey(k)}
                      aria-label={k.id === "back" ? "1文字削除" : isEnter ? "確定" : undefined}
                      className={isEnter ? "btn--hi" : undefined}
                      style={{ display: "grid", placeItems: "center", padding: 16, borderRadius: 12, border: isEnter ? 0 : "1px solid var(--line-2)", background: isEnter ? "var(--hi)" : "var(--surface)", color: isEnter ? "#10130a" : undefined, fontFamily: "var(--font-mono)", fontSize: 20 }}
                    >
                      {k.id === "digit" ? k.digit : <Icon name={k.icon} size={22} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="jp" style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, marginTop: 22 }}>
            続行することで利用規約に同意したものとみなされます
          </p>
        </div>
      </div>
    </div>
  );
}

const splitStyle = {
  flex: 1,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
  minHeight: "100dvh",
} as const;
const posterStyle = {
  background: "#06070a",
  color: "var(--ink)",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "clamp(24px,5vw,40px)",
  minHeight: "min(100dvh, 560px)",
} as const;
// Background photo: cover the whole poster, biased to the upper body, gently graded (darker + a touch
// more contrast) so it reads as an editorial duotone rather than a raw stock photo.
const posterBgStyle = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center 26%",
  filter: "brightness(.82) contrast(1.06) saturate(.9)",
  zIndex: 0,
} as const;
// Left-weighted + top/bottom scrim — anchors the (left-aligned) text without veiling the subject.
const posterScrimStyle = {
  position: "absolute",
  inset: 0,
  zIndex: 0,
  background:
    "linear-gradient(90deg, rgba(6,7,10,.92) 0%, rgba(6,7,10,.6) 40%, rgba(6,7,10,.14) 76%, rgba(6,7,10,.04) 100%)," +
    "linear-gradient(180deg, rgba(6,7,10,.55) 0%, rgba(6,7,10,.05) 24%, rgba(6,7,10,0) 56%, rgba(6,7,10,.6) 100%)",
} as const;
